/**
 * Authentication Helper
 * Handles user permissions and access control
 */

import { useSelector, useStore } from 'react-redux';

/**
 * Selector function for getting current user from Redux state
 * Can be used with useSelector hook or store.getState()
 */
const selectCurrentUser = (state) => {
    return state.session?.userDetails || null;
}

/**
 * React hook to get current user from Redux session state
 * Use this in React components
 */
export function useCurrentUser() {
    return useSelector(selectCurrentUser);
}

/**
 * React hook to get Redux store instance
 * Use this in React components to access store in non-hook contexts
 */
export function useReduxStore() {
    return useStore();
}

/**
 * Get current user from store using selector (for use in callbacks within React components)
 * Use useCurrentUser() hook instead when possible
 */
export function getCurrentUserFromStore(store) {
    if (!store) {
        return null;
    }
    return selectCurrentUser(store.getState());
}

/**
 * React hook to check if user has admin access
 * Use this in React components
 */
export function useAdminAccess() {
    const user = useCurrentUser();
    
    if (!user) {
        return false;
    }
    
    // Check if user is superuser/staff
    if (user.is_superuser || user.is_staff) {
        return true;
    }
    
    // Check if user has subscription management permission
    if (user.permissions?.includes('manage_subscriptions')) {
        return true;
    }
    
    return false;
}

/**
 * Check if user has admin access (accepts user as parameter)
 * Use this in non-React contexts - pass user from useCurrentUser() hook
 */
export function checkAdminAccess(user) {
    if (!user) {
        return false;
    }
    
    // Check if user is superuser/staff
    if (user.is_superuser || user.is_staff) {
        return true;
    }
    
    // Check if user has subscription management permission
    if (user.permissions?.includes('manage_subscriptions')) {
        return true;
    }
    
    return false;
}

/**
 * Get extensions owned by a user
 * TODO: This should call an API endpoint to get user's owned extensions
 */
function getOwnedExtensions(userId) {
    // For now, return empty array
    // In production, this should call: /api/v1/extensions/?owner_id=${userId}
    return [];
}

/**
 * Check if user can manage a specific extension (accepts user as parameter)
 */
export function canManageExtension(user, extensionKey) {
    if (!user) {
        return false;
    }
    
    // Admins can manage all extensions
    if (user.is_superuser || user.is_staff) {
        return true;
    }
    
    // Check if user owns this specific extension
    const ownedExtensions = getOwnedExtensions(user.id);
    return ownedExtensions.some(ext => ext.key === extensionKey);
}

/**
 * Require admin access - returns boolean (accepts user as parameter)
 */
export function requireAdminAccess(user) {
    const hasAccess = checkAdminAccess(user);
    if (!hasAccess) {
        showUnauthorizedMessage();
        return false;
    }
    return true;
}

/**
 * Show unauthorized message to user
 */
export function showUnauthorizedMessage(message = 'You do not have permission to perform this action') {
    const BodaProContext = window.BodaProContext;
    if (BodaProContext) {
        BodaProContext.showToast({
            message,
            intent: 'danger',
            timeout: 5000
        });
    }
}

